import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "form-group mb-2" }
const _hoisted_2 = { class: "row" }
const _hoisted_3 = { class: "my-3" }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = { class: "form-group mb-2" }
const _hoisted_6 = {
  class: "btn-group btn-group-sm",
  role: "group",
  "aria-label": "Add extra field by type"
}
const _hoisted_7 = {
  type: "button",
  disabled: "",
  class: "btn btn-success btn-sm"
}
const _hoisted_8 = ["title", "onClick"]

import { capitalize } from '@/helpers/filters'
import { extraFieldTypes } from '@/components/calendar/EventExtraField/data'
import {
  AnyEventExtraField,
  Calendar,
  DistanceEventExtraField,
  Event,
  GeolocationEventExtraField,
  PhotosEventExtraField,
  PriceEventExtraField,
  VolumeEventExtraField
} from '@/types/api/calendar'
import { ListResponse, Uuid } from '@/types/api'
import { BaseLock, BaseProps, FormManager } from '@/uiManagers/FormManager'
import { computed, onBeforeMount, reactive, ref } from 'vue'
import * as moment from 'moment'
import { PromiseParallelAction } from '@/helpers/tools'
import { Location } from '@/types/app'
import { EventWrite } from '@/api/EventsApi'
import { calendarsApi, eventsApi } from '@/di/api'
import eventBus from '@/di/eventBus'
import { useRouter } from "vue-router";
import { FormKitSelectOption } from "@/types/app.tmp";
import AppComponentHeader from "@/components/app/AppComponentHeader.vue";
import AppMap from "@/components/app/AppMap.vue";
import AppUploadImages from "@/components/app/AppUploadImages.vue";
import AppIcon from "@/components/app/AppIcon.vue";
import FormKitSubmitButtonContent from "@/components/app/FormKit/FormKitSubmitButtonContent.vue";

type Model = EventWrite;

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface Lock extends BaseLock {

}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface Props extends BaseProps {
  itemId?: Uuid
}

interface AddedExtraField {
  type: string,
  origin?: AnyEventExtraField
}

interface Data {
  calendars: Calendar[],
  addedExtraFields: AddedExtraField[],
  geolocationsUnique: GeolocationEventExtraField[],
}

interface TooltipedLocation extends Location {
  tooltip: string,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'EventForm',
  props: {
    itemId: null
  },
  setup(__props: any) {

const props = __props as {
  itemId?: Uuid
};

class EventFormManager extends FormManager<Event, Props, Lock> {

}

const router = useRouter()



const model = ref<Model>({
  title: '',
  description: '',
  action_at: moment().format('YYYY-MM-DD'),
  calendar_id: null,
  extra_fields: []
})

const configModel = ref<{ disableAutocomplete: boolean }>({ disableAutocomplete: true })

const data = reactive<Data>({
  calendars: [],
  addedExtraFields: [],
  geolocationsUnique: []
})

const lock = reactive<Lock>({ formSubmitting: false, preparing: false })

const eventFormManager: EventFormManager = new EventFormManager(
    'event',
    router,
    eventBus,
    props,
    (): Model => ({
      title: model.value.title,
      description: model.value.description,
      action_at: model.value.action_at,
      calendar_id: model.value.calendar_id,
      extra_fields: model.value.extra_fields.map((eventExtraField: Partial<AnyEventExtraField>, index): AnyEventExtraField => {
        eventExtraField.weight = index
        eventExtraField.id = eventExtraField.id || undefined

        return eventExtraField as AnyEventExtraField
      })
    }),
    lock,
    {
      prepareFormActions: [
        {
          promise: (): Promise<GeolocationEventExtraField[]> => eventsApi.getEventExtraFieldGeolocationsUnique(),
          resolve: (list): void => {
            data.geolocationsUnique = list
          }
        } as PromiseParallelAction<GeolocationEventExtraField[]>,
        {
          promise: (): Promise<ListResponse<Calendar>> => calendarsApi.getCalendars(),
          resolve: (list): void => {
            data.calendars = list.items
            model.value.calendar_id = data.calendars.filter((calendar: Calendar): boolean => calendar.is_default)[0].id
          }
        } as PromiseParallelAction<ListResponse<Calendar>>
      ],
      resolveItem: (itemId: Uuid): PromiseParallelAction<Event> => ({
        promise: () => eventsApi.getEvent(itemId),
        resolve: (event): void => {
          model.value.title = event.title
          model.value.description = event.description || ''
          model.value.action_at = moment(event.action_at).format('YYYY-MM-DD')
          model.value.calendar_id = event.calendar.id

          if (event.extra_fields.length > 0) {
            event.extra_fields.forEach((eventExtraField, index) => {
              addExtraField(eventExtraField.type, eventExtraField, index)
            })
          }
        }
      } as PromiseParallelAction<Event>),
      create: ((submitData: Model): Promise<void> => eventsApi.createEvent(submitData)) as (submitData: object) => Promise<void>,
      update: ((itemId: Uuid, submitData: Model): Promise<void> => eventsApi.updateEvent(itemId, submitData)) as (id: Uuid, submitData: object) => Promise<void>
    },
    {},
    '/cabinet/events'
)

const geolocationsUnique = computed<TooltipedLocation[]>(() => {
  return data.geolocationsUnique.map((geolocation: GeolocationEventExtraField) => ({
    tooltip: geolocation.name,
    lat: geolocation.lat,
    lon: geolocation.lon
  }))
})

const calendarOptions = computed<FormKitSelectOption[]>((): FormKitSelectOption[] => {
  return data.calendars.map((calendar) => ({ label: calendar.title, value: calendar.id }))
})

const extraFieldTypeButtons = computed<{ type: string, name: string, icon: string }[]>(() => {
  return extraFieldTypes.map(({ type, name, icon }) => ({ type, name, icon }))
})

const isUpdate = computed<boolean>((): boolean => eventFormManager.isUpdate())

function addExtraField (type: string, eventExtraField: AnyEventExtraField, index: number): void {
  index = index || model.value.extra_fields.length

  const addedExtraField: { type: string, origin?: AnyEventExtraField } = { type }
  if (eventExtraField) {
    addedExtraField.origin = eventExtraField
  }

  data.addedExtraFields.push(addedExtraField)

  model.value.extra_fields[index] = ((eef: Partial<AnyEventExtraField>): AnyEventExtraField => {
    const _model = {
      id: eef.id,
      name: eef.name,
      type: eef.type
    }

    switch (eef.type) {
    case 'price':
      (_model as PriceEventExtraField).amount = (eef as PriceEventExtraField).amount;
      (_model as PriceEventExtraField).currency = (eef as PriceEventExtraField).currency
      break
    case 'distance':
      (_model as DistanceEventExtraField).distance = (eef as DistanceEventExtraField).distance;
      (_model as DistanceEventExtraField).unit = (eef as DistanceEventExtraField).unit
      break
    case 'volume':
      (_model as VolumeEventExtraField).volume = (eef as VolumeEventExtraField).volume;
      (_model as VolumeEventExtraField).unit = (eef as VolumeEventExtraField).unit
      break
    case 'geolocation':
      (_model as GeolocationEventExtraField).lat = (eef as GeolocationEventExtraField).lat;
      (_model as GeolocationEventExtraField).lon = (eef as GeolocationEventExtraField).lon
      break
    case 'photos':
      (_model as PhotosEventExtraField).photos = [...(eef as PhotosEventExtraField).photos || []]
      break
    default:
      throw new Error(`Unsupported type: ${eef.type}`)
    }

    return _model as AnyEventExtraField
  })(eventExtraField || { type })
}

function removeExtraField (index: number) {
  data.addedExtraFields = data.addedExtraFields.filter((_, idx: number): boolean => idx !== index)
}

function fetchEventNameSuggestions (query: string): Promise<string[]> {
  return eventsApi.getEventNameSuggestions(query)
}

function fetchEventExtraFieldSuggestions (type: string) {
  return (query: string): Promise<string[]> => eventsApi.getExtraFieldNameSuggestions(type, query)
}

function onGeolocationSelected (index: number) {
  return (location: Location) => {
    const geolocation: GeolocationEventExtraField = model.value.extra_fields[index] as GeolocationEventExtraField

    geolocation.lat = location.lat
    geolocation.lon = location.lon
  }
}

function onFileUploadDone (index: number) {
  return (photos: []): void => {
    (model.value.extra_fields[index] as PhotosEventExtraField).photos = photos
  }
}

const submit = eventFormManager.getSubmit()

onBeforeMount(async (): Promise<void> => {
  await eventFormManager.prepareForm()
})


return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_FormKit, {
      modelValue: configModel.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((configModel).value = $event)),
      type: "form",
      actions: false
    }, {
      default: _withCtx(() => [
        _createVNode(_component_FormKit, {
          type: "checkbox",
          name: "disableAutocomplete",
          label: "Disable autocomplete in form"
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_FormKit, {
      modelValue: model.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((model).value = $event)),
      type: "form",
      actions: false,
      onSubmit: _unref(submit)
    }, {
      default: _withCtx(() => [
        _createVNode(_component_FormKit, {
          type: "autocomplete",
          name: "title",
          label: "Title",
          "fetch-suggestions": fetchEventNameSuggestions,
          "disable-autocomplete": configModel.value.disableAutocomplete,
          disabled: _unref(isUpdate) && lock.preparing,
          validation: "required",
          "outer-class": "mb-3"
        }, null, 8, ["disable-autocomplete", "disabled"]),
        _createVNode(_component_FormKit, {
          type: "textarea",
          name: "description",
          label: "Description",
          disabled: _unref(isUpdate) && lock.preparing,
          "outer-class": "mb-3"
        }, null, 8, ["disabled"]),
        _createVNode(_component_FormKit, {
          type: "date",
          name: "action_at",
          label: "Action at",
          disabled: _unref(isUpdate) && lock.preparing,
          "outer-class": "mb-3"
        }, null, 8, ["disabled"]),
        _createVNode(_component_FormKit, {
          type: "select",
          name: "calendar_id",
          label: "Calendar",
          disabled: lock.preparing,
          options: _unref(calendarOptions),
          "outer-class": "mb-3"
        }, null, 8, ["disabled", "options"]),
        _createVNode(_component_FormKit, {
          id: "event-extra-fields",
          type: "list",
          name: "extra_fields",
          class: "form-group"
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              _createElementVNode("div", _hoisted_2, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(data.addedExtraFields, (extraEventField, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: `extra-event-field-${index}`,
                    class: "col-md-4"
                  }, [
                    _createVNode(_component_FormKit, {
                      ref_for: true,
                      ref: `eventExtraFieldGroup_${index}`,
                      type: "group"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(AppComponentHeader, { class: "mt-4" }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(capitalize)(extraEventField.type)), 1)
                          ]),
                          _: 2
                        }, 1024),
                        _createVNode(_component_FormKit, {
                          type: "autocomplete",
                          name: "name",
                          label: "Name",
                          validation: "required",
                          "fetch-suggestions": fetchEventExtraFieldSuggestions(extraEventField.type),
                          "disable-autocomplete": configModel.value.disableAutocomplete,
                          "outer-class": "mb-3"
                        }, null, 8, ["fetch-suggestions", "disable-autocomplete"]),
                        _createVNode(_component_FormKit, {
                          type: "hidden",
                          name: "type",
                          validation: "required"
                        }),
                        (extraEventField.type === 'price')
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                              _createVNode(_component_FormKit, {
                                type: "number",
                                name: "amount",
                                label: "Price",
                                step: ".01",
                                validation: "required",
                                "outer-class": "mb-3"
                              }),
                              _createVNode(_component_FormKit, {
                                type: "select",
                                name: "currency",
                                label: "Currency",
                                options: { UAH: 'UAH', USD: 'USD', EUR: 'EUR' },
                                "outer-class": "mb-3"
                              })
                            ], 64))
                          : _createCommentVNode("", true),
                        (extraEventField.type === 'distance')
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                              _createVNode(_component_FormKit, {
                                type: "number",
                                name: "distance",
                                label: "Distance",
                                step: ".01",
                                validation: "required",
                                "outer-class": "mb-3"
                              }),
                              _createVNode(_component_FormKit, {
                                type: "select",
                                name: "unit",
                                label: "Unit",
                                options: { km: 'Kilometers', m: 'Meters' },
                                "outer-class": "mb-3"
                              })
                            ], 64))
                          : _createCommentVNode("", true),
                        (extraEventField.type === 'volume')
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                              _createVNode(_component_FormKit, {
                                type: "number",
                                name: "volume",
                                label: "Volume",
                                step: ".01",
                                validation: "required",
                                "outer-class": "mb-3"
                              }),
                              _createVNode(_component_FormKit, {
                                type: "select",
                                name: "unit",
                                label: "Unit",
                                options: { l: 'Liters' },
                                "outer-class": "mb-3"
                              })
                            ], 64))
                          : _createCommentVNode("", true),
                        (extraEventField.type === 'geolocation')
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                              _createVNode(_component_FormKit, {
                                type: "hidden",
                                name: "lat"
                              }),
                              _createVNode(_component_FormKit, {
                                type: "hidden",
                                name: "lon"
                              }),
                              _createVNode(AppMap, {
                                zoom: 19,
                                size: {height: '300px', width: '100%'},
                                center: extraEventField.origin ? {lat: extraEventField.origin.lat, lon: extraEventField.origin.lon} : { lon: 0, lat: 0 },
                                markers: _unref(geolocationsUnique),
                                class: "mt-4",
                                onCenterChanged: (e) => onGeolocationSelected(index)(e)
                              }, null, 8, ["center", "markers", "onCenterChanged"])
                            ], 64))
                          : _createCommentVNode("", true),
                        (extraEventField.type === 'photos')
                          ? (_openBlock(), _createBlock(AppUploadImages, {
                              key: 4,
                              images: extraEventField.origin?.photos || [],
                              onFileUploadDone: (e) => onFileUploadDone(index)(e)
                            }, null, 8, ["images", "onFileUploadDone"]))
                          : _createCommentVNode("", true),
                        _createElementVNode("div", _hoisted_3, [
                          _createElementVNode("button", {
                            type: "button",
                            class: "btn btn-sm btn-secondary",
                            onClick: ($event: any) => (removeExtraField(index))
                          }, " Remove extra field ", 8, _hoisted_4)
                        ])
                      ]),
                      _: 2
                    }, 1536)
                  ]))
                }), 128))
              ])
            ]),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("button", _hoisted_7, [
                  _createVNode(AppIcon, { icon: "plus" })
                ]),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(extraFieldTypeButtons), (extraFieldType) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: `add-extra-field-type-${extraFieldType.type}-button`,
                    type: "button",
                    class: "btn btn-light btn-sm",
                    "data-bs-toggle": "tooltip",
                    "data-bs-placement": "top",
                    title: extraFieldType.name,
                    onClick: ($event: any) => (addExtraField(extraFieldType.type))
                  }, [
                    _createVNode(AppIcon, {
                      icon: extraFieldType.icon
                    }, null, 8, ["icon"])
                  ], 8, _hoisted_8))
                }), 128))
              ])
            ]),
            _createVNode(_component_FormKit, {
              type: "submit",
              "input-class": "btn btn-primary btn-sm"
            }, {
              default: _withCtx(() => [
                _createVNode(FormKitSubmitButtonContent, null, {
                  default: _withCtx(() => [
                    _createTextVNode("Save")
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue", "onSubmit"])
  ], 64))
}
}

})